// Comprehensive country guides for the 8 core countries

export interface KoreanCity {
  name: string;
  koreanPopulation: string;
  koreanNeighborhood?: string;
  highlights: string[];
}

export interface CostOfLiving {
  rentOneBedroom: string;
  groceriesMonthly: string;
  publicTransport: string;
  mealOut: string;
  notes?: string;
}

export interface CountryGuide {
  code: string;
  name: string;
  nameKo: string;
  flag: string;
  koreanPopulation: string;
  koreanPopulationNote: string;
  overview: string;
  majorCities: KoreanCity[];
  costOfLiving: CostOfLiving;
  koreanCommunity: {
    churches: string;
    schools: string;
    markets: string;
    organizations: string[];
  };
  practicalInfo: {
    language: string;
    currency: string;
    timezone: string;
    drivingSide: string;
    electricPlug: string;
    emergencyNumber: string;
  };
  livingTips: string[];
  challenges: string[];
  resources: Array<{
    name: string;
    url: string;
    description: string;
  }>;
}

export const COUNTRY_GUIDES: CountryGuide[] = [
  // United States
  {
    code: 'US',
    name: 'United States',
    nameKo: '미국',
    flag: '🇺🇸',
    koreanPopulation: '2.5 million+',
    koreanPopulationNote: 'Largest Korean diaspora outside Korea',
    overview: 'The United States is home to the largest Korean population outside of Korea. Korean Americans have built vibrant communities with extensive infrastructure including churches, schools, businesses, and cultural centers. Major hubs include Los Angeles (Koreatown), New York/New Jersey, and the greater Washington D.C. area.',
    majorCities: [
      {
        name: 'Los Angeles, CA',
        koreanPopulation: '350,000+',
        koreanNeighborhood: 'Koreatown (K-Town)',
        highlights: ['Largest Koreatown in the US', 'Korean BBQ capital', 'H Mart headquarters', 'Korean spas (jjimjilbang)'],
      },
      {
        name: 'New York / New Jersey',
        koreanPopulation: '250,000+',
        koreanNeighborhood: 'Flushing, Fort Lee, Palisades Park',
        highlights: ['Strong business community', 'Korean supermarkets', 'Multiple Korean churches', 'K-12 Korean schools'],
      },
      {
        name: 'Washington D.C. / Virginia',
        koreanPopulation: '100,000+',
        koreanNeighborhood: 'Annandale, Centreville',
        highlights: ['Government/professional community', 'Korean restaurants', 'Cultural events'],
      },
      {
        name: 'Atlanta, GA',
        koreanPopulation: '80,000+',
        koreanNeighborhood: 'Duluth, Johns Creek',
        highlights: ['Growing community', 'Affordable living', 'Korean businesses'],
      },
    ],
    costOfLiving: {
      rentOneBedroom: '$1,500 - $3,500/month (varies by city)',
      groceriesMonthly: '$400 - $600',
      publicTransport: '$100 - $150/month (in major cities)',
      mealOut: '$15 - $25',
      notes: 'LA and NYC are significantly more expensive. Consider Texas or Georgia for lower costs.',
    },
    koreanCommunity: {
      churches: '5,000+ Korean churches nationwide',
      schools: '200+ Korean language schools',
      markets: 'H Mart, Zion Market, Hannam Chain',
      organizations: [
        'Korean American Coalition',
        'Korean American Chamber of Commerce',
        'Korean Community Service Center',
      ],
    },
    practicalInfo: {
      language: 'English',
      currency: 'USD ($)',
      timezone: 'UTC-5 to UTC-10',
      drivingSide: 'Right',
      electricPlug: 'Type A/B (120V)',
      emergencyNumber: '911',
    },
    livingTips: [
      'Build credit history early - needed for apartments, phones, loans',
      'Get a Social Security Number (SSN) as soon as eligible',
      'Health insurance is essential - employer plans or ACA marketplace',
      'Join local Korean community Facebook groups for tips and connections',
      'Korean groceries deliver nationwide via H Mart online',
    ],
    challenges: [
      'High healthcare costs without insurance',
      'Work visa limitations and green card backlogs',
      'Cost of living in major Korean hubs is high',
      'Need car in most cities (except NYC)',
    ],
    resources: [
      { name: 'Korean American Coalition', url: 'https://kacla.org', description: 'Community advocacy and resources' },
      { name: 'H Mart', url: 'https://www.hmart.com', description: 'Korean grocery delivery nationwide' },
      { name: 'Korean Consulate LA', url: 'https://overseas.mofa.go.kr/us-losangeles-en', description: 'Official consular services' },
    ],
  },

  // Canada
  {
    code: 'CA',
    name: 'Canada',
    nameKo: '캐나다',
    flag: '🇨🇦',
    koreanPopulation: '240,000+',
    koreanPopulationNote: 'Growing community with strong immigration pathways',
    overview: 'Canada offers a welcoming environment for Korean immigrants with relatively accessible immigration pathways like Express Entry. The Korean community is concentrated in Toronto and Vancouver, with robust Korean business districts, schools, and cultural centers.',
    majorCities: [
      {
        name: 'Toronto, ON',
        koreanPopulation: '80,000+',
        koreanNeighborhood: 'North York (Yonge & Finch), Koreatown (Christie)',
        highlights: ['Largest Korean community in Canada', 'Korean churches', 'H Mart locations', 'Korean bakeries'],
      },
      {
        name: 'Vancouver, BC',
        koreanPopulation: '60,000+',
        koreanNeighborhood: 'Coquitlam, Burnaby',
        highlights: ['Mild climate', 'Asian-influenced culture', 'Korean restaurants', 'Tech industry jobs'],
      },
    ],
    costOfLiving: {
      rentOneBedroom: 'CAD $1,800 - $2,500/month',
      groceriesMonthly: 'CAD $400 - $500',
      publicTransport: 'CAD $150/month',
      mealOut: 'CAD $18 - $30',
      notes: 'Toronto and Vancouver are expensive. Consider Calgary or Ottawa for lower costs.',
    },
    koreanCommunity: {
      churches: '200+ Korean churches',
      schools: 'Multiple Korean language schools',
      markets: 'H Mart, PAT Central, Galleria',
      organizations: [
        'Korean Canadian Cultural Association',
        'Korean Canadian Business Association',
      ],
    },
    practicalInfo: {
      language: 'English, French',
      currency: 'CAD ($)',
      timezone: 'UTC-3.5 to UTC-8',
      drivingSide: 'Right',
      electricPlug: 'Type A/B (120V)',
      emergencyNumber: '911',
    },
    livingTips: [
      'Apply for provincial health insurance immediately upon arrival',
      'Get a SIN (Social Insurance Number) for work',
      'Express Entry is fastest path to permanent residence',
      'Winter clothing is essential - invest in quality gear',
      'Join Korean Canadian Facebook groups for your city',
    ],
    challenges: [
      'Harsh winters, especially in Toronto',
      'High housing costs in Vancouver and Toronto',
      'Credential recognition can be challenging',
      'Job market can be competitive for new immigrants',
    ],
    resources: [
      { name: 'IRCC', url: 'https://www.canada.ca/en/immigration-refugees-citizenship.html', description: 'Official immigration information' },
      { name: 'Korean Consulate Toronto', url: 'https://overseas.mofa.go.kr/ca-toronto-en', description: 'Consular services' },
      { name: 'Settlement.org', url: 'https://settlement.org', description: 'Newcomer resources for Ontario' },
    ],
  },

  // Mexico
  {
    code: 'MX',
    name: 'Mexico',
    nameKo: '멕시코',
    flag: '🇲🇽',
    koreanPopulation: '15,000+',
    koreanPopulationNote: 'Growing expat and business community',
    overview: 'Mexico has become increasingly popular for Korean expats and digital nomads due to its proximity to the US, affordable cost of living, and favorable visa policies. The Korean community is primarily in Mexico City, with growing numbers in Guadalajara and coastal areas popular with remote workers.',
    majorCities: [
      {
        name: 'Mexico City',
        koreanPopulation: '10,000+',
        koreanNeighborhood: 'Zona Rosa, Polanco',
        highlights: ['Korean embassy', 'Korean restaurants', 'Cultural center', 'Business community'],
      },
      {
        name: 'Guadalajara',
        koreanPopulation: '2,000+',
        highlights: ['Tech hub', 'Lower cost than CDMX', 'Growing Korean presence'],
      },
    ],
    costOfLiving: {
      rentOneBedroom: '$500 - $1,200/month',
      groceriesMonthly: '$200 - $300',
      publicTransport: '$20 - $40/month',
      mealOut: '$5 - $15',
      notes: 'Very affordable compared to US/Canada. Mexico City is most expensive.',
    },
    koreanCommunity: {
      churches: 'Several Korean churches in CDMX',
      schools: 'Limited Korean schools',
      markets: 'Asian markets in major cities, online delivery options',
      organizations: [
        'Korean Association of Mexico',
        'KOTRA Mexico',
      ],
    },
    practicalInfo: {
      language: 'Spanish',
      currency: 'MXN (Peso)',
      timezone: 'UTC-6 to UTC-8',
      drivingSide: 'Right',
      electricPlug: 'Type A/B (127V)',
      emergencyNumber: '911',
    },
    livingTips: [
      'Learn basic Spanish - English is not widely spoken',
      'Use apps like Uber, Rappi for transportation and delivery',
      'Private health insurance is affordable and recommended',
      'Digital nomads can stay 180 days on tourist visa',
      'Temporary Resident visa allows longer stays for remote workers',
    ],
    challenges: [
      'Language barrier if you don\'t speak Spanish',
      'Limited Korean community infrastructure',
      'Safety concerns in some areas',
      'Banking can be difficult for foreigners',
    ],
    resources: [
      { name: 'Korean Embassy Mexico', url: 'https://overseas.mofa.go.kr/mx-es', description: 'Embassy services' },
      { name: 'Internations Mexico', url: 'https://www.internations.org/mexico-city-expats', description: 'Expat community' },
    ],
  },

  // United Kingdom
  {
    code: 'GB',
    name: 'United Kingdom',
    nameKo: '영국',
    flag: '🇬🇧',
    koreanPopulation: '45,000+',
    koreanPopulationNote: 'Strong student and professional community',
    overview: 'The UK is home to a significant Korean community, particularly in London. Many Koreans come for education at prestigious universities or professional opportunities. New Malden in South London is known as "Little Korea" and has the largest Korean population in Europe.',
    majorCities: [
      {
        name: 'London',
        koreanPopulation: '35,000+',
        koreanNeighborhood: 'New Malden',
        highlights: ['Largest Koreatown in Europe', 'Korean restaurants and markets', 'Korean schools', 'Cultural events'],
      },
      {
        name: 'Manchester',
        koreanPopulation: '3,000+',
        highlights: ['University students', 'Growing Korean restaurants'],
      },
    ],
    costOfLiving: {
      rentOneBedroom: '£1,200 - £2,500/month (London)',
      groceriesMonthly: '£300 - £400',
      publicTransport: '£150 - £200/month (London)',
      mealOut: '£12 - £25',
      notes: 'London is very expensive. Consider Manchester, Birmingham for lower costs.',
    },
    koreanCommunity: {
      churches: '50+ Korean churches in UK',
      schools: 'Korean School of London, Saturday schools',
      markets: 'H Mart, Korea Foods, Centre Point',
      organizations: [
        'Korean Residents Society in the UK',
        'Korean British Chamber of Commerce',
      ],
    },
    practicalInfo: {
      language: 'English',
      currency: 'GBP (£)',
      timezone: 'UTC+0/+1 (BST)',
      drivingSide: 'Left',
      electricPlug: 'Type G (230V)',
      emergencyNumber: '999',
    },
    livingTips: [
      'Register with NHS GP for free healthcare',
      'Get a National Insurance Number for work',
      'Oyster card or contactless for London transport',
      'New Malden has the best Korean food scene in Europe',
      'Youth Mobility Visa available for ages 18-30',
    ],
    challenges: [
      'High cost of living, especially London',
      'Post-Brexit visa restrictions',
      'Weather can be challenging (rain, gray skies)',
      'Competitive job market',
    ],
    resources: [
      { name: 'Korean Embassy UK', url: 'https://overseas.mofa.go.kr/gb-en', description: 'Embassy services' },
      { name: 'GOV.UK Visas', url: 'https://www.gov.uk/browse/visas-immigration', description: 'Official visa information' },
      { name: 'Korea Foods', url: 'https://www.koreafoods.co.uk', description: 'Korean grocery delivery' },
    ],
  },

  // Australia
  {
    code: 'AU',
    name: 'Australia',
    nameKo: '호주',
    flag: '🇦🇺',
    koreanPopulation: '180,000+',
    koreanPopulationNote: 'Popular for students and Working Holiday makers',
    overview: 'Australia is extremely popular with young Koreans, especially through the Working Holiday Visa program. Sydney has a vibrant Koreatown in Strathfield, while Melbourne offers a growing Korean community. The lifestyle, climate, and relative proximity to Korea make it attractive.',
    majorCities: [
      {
        name: 'Sydney, NSW',
        koreanPopulation: '80,000+',
        koreanNeighborhood: 'Strathfield, Eastwood',
        highlights: ['Largest Korean community in Australia', 'Koreatown in Strathfield', 'Korean churches', 'Asian grocers'],
      },
      {
        name: 'Melbourne, VIC',
        koreanPopulation: '50,000+',
        koreanNeighborhood: 'Box Hill, Glen Waverley',
        highlights: ['Growing community', 'Korean restaurants', 'Cultural events'],
      },
      {
        name: 'Brisbane, QLD',
        koreanPopulation: '20,000+',
        highlights: ['Affordable living', 'Warm climate', 'Student community'],
      },
    ],
    costOfLiving: {
      rentOneBedroom: 'AUD $1,800 - $2,800/month (Sydney)',
      groceriesMonthly: 'AUD $400 - $500',
      publicTransport: 'AUD $150/month',
      mealOut: 'AUD $18 - $30',
      notes: 'Sydney is most expensive. Brisbane and Adelaide offer lower costs.',
    },
    koreanCommunity: {
      churches: '200+ Korean churches',
      schools: 'Korean Saturday schools in major cities',
      markets: 'H Mart, Korean supermarkets in Strathfield',
      organizations: [
        'Korean Society of Sydney',
        'Korean Australian Association',
      ],
    },
    practicalInfo: {
      language: 'English',
      currency: 'AUD ($)',
      timezone: 'UTC+8 to UTC+11',
      drivingSide: 'Left',
      electricPlug: 'Type I (230V)',
      emergencyNumber: '000',
    },
    livingTips: [
      'Working Holiday Visa (417) available for ages 18-30',
      'Get a TFN (Tax File Number) immediately for work',
      'Open a bank account before arriving (some banks allow this)',
      'Medicare available for some visa holders',
      'Strathfield is the go-to for Korean food in Sydney',
    ],
    challenges: [
      'High cost of living in Sydney and Melbourne',
      'Far from Korea (9+ hour flights)',
      'Strict biosecurity rules (food/plant restrictions)',
      'Sun protection essential (high UV)',
    ],
    resources: [
      { name: 'Korean Embassy Australia', url: 'https://overseas.mofa.go.kr/au-en', description: 'Embassy services' },
      { name: 'Home Affairs', url: 'https://immi.homeaffairs.gov.au', description: 'Visa and immigration' },
      { name: 'Study in Australia', url: 'https://www.studyaustralia.gov.au', description: 'Student information' },
    ],
  },

  // Indonesia
  {
    code: 'ID',
    name: 'Indonesia',
    nameKo: '인도네시아',
    flag: '🇮🇩',
    koreanPopulation: '40,000+',
    koreanPopulationNote: 'Business community and digital nomads in Bali',
    overview: 'Indonesia attracts Korean business professionals (especially in Jakarta) and digital nomads (especially in Bali). The low cost of living, warm climate, and new digital nomad visa make it increasingly popular. Korean businesses have significant presence in manufacturing and trade.',
    majorCities: [
      {
        name: 'Jakarta',
        koreanPopulation: '25,000+',
        koreanNeighborhood: 'Kelapa Gading, Pluit',
        highlights: ['Korean business community', 'Korean schools', 'Korean restaurants', 'Korean churches'],
      },
      {
        name: 'Bali',
        koreanPopulation: '5,000+',
        koreanNeighborhood: 'Canggu, Seminyak',
        highlights: ['Digital nomad hub', 'Korean cafes', 'Coworking spaces', 'Beach lifestyle'],
      },
    ],
    costOfLiving: {
      rentOneBedroom: '$300 - $800/month',
      groceriesMonthly: '$150 - $250',
      publicTransport: '$30 - $50/month',
      mealOut: '$3 - $10',
      notes: 'Very affordable. Bali tourist areas are more expensive than Jakarta.',
    },
    koreanCommunity: {
      churches: 'Korean churches in Jakarta and other cities',
      schools: 'Jakarta Korea International School',
      markets: 'Korean marts in Kelapa Gading, limited in Bali',
      organizations: [
        'Korean Association of Indonesia',
        'KOTRA Jakarta',
      ],
    },
    practicalInfo: {
      language: 'Indonesian (Bahasa Indonesia)',
      currency: 'IDR (Rupiah)',
      timezone: 'UTC+7 to UTC+9',
      drivingSide: 'Left',
      electricPlug: 'Type C/F (230V)',
      emergencyNumber: '112',
    },
    livingTips: [
      'B211A Digital Nomad Visa available for remote workers',
      'Grab app is essential for transport and food delivery',
      'Private health insurance strongly recommended',
      'Learn basic Indonesian - very helpful',
      'Bali has strong coworking scene (Dojo, Outpost)',
    ],
    challenges: [
      'Traffic congestion in Jakarta',
      'Limited Korean community infrastructure outside Jakarta',
      'Visa requirements can be complex',
      'Internet can be unreliable in some areas',
    ],
    resources: [
      { name: 'Korean Embassy Indonesia', url: 'https://overseas.mofa.go.kr/id-en', description: 'Embassy services' },
      { name: 'Indonesia Immigration', url: 'https://www.imigrasi.go.id', description: 'Visa information' },
    ],
  },

  // China
  {
    code: 'CN',
    name: 'China',
    nameKo: '중국',
    flag: '🇨🇳',
    koreanPopulation: '2 million+',
    koreanPopulationNote: 'Second largest Korean diaspora (ethnic Koreans + expatriates)',
    overview: 'China has one of the largest Korean populations, including both ethnic Korean-Chinese (Joseonjok/조선족) and Korean expatriates. The business community is significant in Shanghai, Beijing, and cities near Korea like Qingdao and Dalian. Yanbian Korean Autonomous Prefecture has a large ethnic Korean population.',
    majorCities: [
      {
        name: 'Shanghai',
        koreanPopulation: '50,000+ expats',
        koreanNeighborhood: 'Gubei, Hongqiao',
        highlights: ['Business hub', 'Korean school', 'Korean restaurants', 'Korean community center'],
      },
      {
        name: 'Beijing',
        koreanPopulation: '40,000+ expats',
        koreanNeighborhood: 'Wangjing',
        highlights: ['"Little Seoul"', 'Korean businesses', 'Korean churches'],
      },
      {
        name: 'Qingdao',
        koreanPopulation: '30,000+',
        highlights: ['Close to Korea', 'Korean manufacturing', 'Korean food scene'],
      },
    ],
    costOfLiving: {
      rentOneBedroom: '$500 - $1,500/month (varies greatly)',
      groceriesMonthly: '$200 - $400',
      publicTransport: '$30 - $50/month',
      mealOut: '$5 - $15',
      notes: 'Shanghai and Beijing are expensive. Tier 2/3 cities much cheaper.',
    },
    koreanCommunity: {
      churches: 'Korean churches in major cities',
      schools: 'Korean international schools in Shanghai, Beijing',
      markets: 'Korean marts in Korean neighborhoods',
      organizations: [
        'Korean Chamber of Commerce China',
        'Korean Residents Association',
      ],
    },
    practicalInfo: {
      language: 'Mandarin Chinese',
      currency: 'CNY (Yuan/RMB)',
      timezone: 'UTC+8',
      drivingSide: 'Right',
      electricPlug: 'Type A/C/I (220V)',
      emergencyNumber: '110 (police), 120 (ambulance)',
    },
    livingTips: [
      'VPN essential for accessing Korean/Western websites',
      'WeChat and Alipay required for daily life',
      'Z Visa required for work - employer must sponsor',
      'Learn basic Mandarin - very helpful',
      'Air quality varies - check AQI apps',
    ],
    challenges: [
      'Language barrier significant',
      'Internet restrictions (Great Firewall)',
      'Complex visa requirements',
      'Cultural differences in workplace',
    ],
    resources: [
      { name: 'Korean Embassy China', url: 'https://overseas.mofa.go.kr/cn-en', description: 'Embassy services' },
    ],
  },

  // Singapore
  {
    code: 'SG',
    name: 'Singapore',
    nameKo: '싱가포르',
    flag: '🇸🇬',
    koreanPopulation: '25,000+',
    koreanPopulationNote: 'Business and professional community',
    overview: 'Singapore attracts Korean professionals and businesses with its stable environment, English-speaking population, and position as an Asian business hub. The Korean community is well-established with schools, churches, and businesses, though smaller than in other Asian hubs.',
    majorCities: [
      {
        name: 'Singapore',
        koreanPopulation: '25,000+',
        koreanNeighborhood: 'Tanjong Pagar, East Coast',
        highlights: ['Korean restaurants', 'Korean churches', 'Korean school', 'Business community'],
      },
    ],
    costOfLiving: {
      rentOneBedroom: 'SGD $2,500 - $4,000/month',
      groceriesMonthly: 'SGD $400 - $600',
      publicTransport: 'SGD $100 - $150/month',
      mealOut: 'SGD $10 - $25',
      notes: 'Very expensive for housing. Hawker centers offer affordable food.',
    },
    koreanCommunity: {
      churches: '20+ Korean churches',
      schools: 'Korean International School Singapore',
      markets: 'Korean marts, online delivery (RedMart)',
      organizations: [
        'Korean Association of Singapore',
        'Korean Chamber of Commerce Singapore',
      ],
    },
    practicalInfo: {
      language: 'English, Mandarin, Malay, Tamil',
      currency: 'SGD ($)',
      timezone: 'UTC+8',
      drivingSide: 'Left',
      electricPlug: 'Type G (230V)',
      emergencyNumber: '999',
    },
    livingTips: [
      'Employment Pass (EP) needed for professional work',
      'MRT (metro) is efficient and affordable',
      'Hawker centers for cheap, good food',
      'HDB flats available for some visa holders',
      'English widely spoken - easy adjustment',
    ],
    challenges: [
      'Very high housing costs',
      'Small country - can feel limiting',
      'Strict laws (fines for many things)',
      'Competitive job market for foreigners',
    ],
    resources: [
      { name: 'Korean Embassy Singapore', url: 'https://overseas.mofa.go.kr/sg-en', description: 'Embassy services' },
      { name: 'MOM Singapore', url: 'https://www.mom.gov.sg', description: 'Work pass information' },
    ],
  },
];

// Helper function to get guide by country code
export function getCountryGuide(code: string): CountryGuide | undefined {
  return COUNTRY_GUIDES.find(g => g.code === code.toUpperCase());
}
